import { z } from 'zod'

import { Pagination } from '@/types/app/request'
import { Product } from '@/types/catalogue/product'
import { Machine } from '@/types/machines/machine'
import { Site } from '@/types/park/site'
import { KonsoleUser } from '@/types/users/konsole'
import { Konvive, KonviveBinding } from '@/types/users/konvive'

/** Status available for an order. */
export const StatusOrder = z.enum([
    'IN_PREPARATION',
    'ON_DELIVERY',
    'READY',
    'RETRIEVED',
    'CANCELLED',
    'EXPIRED',
    'RETRIEVED_BY_SOMEONE_ELSE',
    'REMOVED',
])

export type StatusOrder = z.infer<typeof StatusOrder>

/** Reason when canceling an order. */
export const ReasonCancelOrder = z.enum(['KONVIVE', 'STOCK', 'OTHER'])
export type ReasonCancelOrder = z.infer<typeof ReasonCancelOrder>

export const ArticleOrder = z.object({
    product: Product,
    is_weight_quantity: z.boolean(),
    quantity: z.number().positive(),
})

export type ArticleOrder = z.infer<typeof ArticleOrder>

export const HistoryOrder = z.object({
    datetime: z.string().datetime({ offset: true }),
    status: StatusOrder,
    author: KonsoleUser,
    changes: ArticleOrder.array(),
})

export const BaseOrder = z.object({
    id: z.number().positive(),

    /** The status. */
    status: StatusOrder,

    /** The site related to the order. */
    site: Site,

    /** Price */
    price: z.number().positive(),

    /** The machine only presents if status is READY or CANCELLED. */
    terminal: Machine.optional(),

    /** The reason only presents when status is CANCELLED. */
    reason: ReasonCancelOrder.optional(),

    /** The articles of the order. */
    articles: ArticleOrder.array().min(1),

    /** The history related to the order. */
    history: HistoryOrder.array().min(1),
})

export const OrderUnion = z.union([
    z.object({
        konvive: KonviveBinding,
    }),
    z.object({
        phone_number: z.string().min(1),
    }),
])

export const Order = BaseOrder.and(OrderUnion)
export type Order = z.infer<typeof Order>

/** Data article sent to create an order. */
const ArticleOrderPayload = ArticleOrder.extend({
    product: Product.shape.id,
})

export const OrderUnionPayload = z.union([
    z.object({
        konvive: Konvive.shape.id,
    }),
    z.object({
        phone_number: z.string().min(1),
    }),
])

/** The data to send when creating an order. */
export const PayloadOrder = z
    .object({
        site: Site.shape.id,
        articles: ArticleOrderPayload.array().min(1),
    })
    .and(OrderUnionPayload)

export type PayloadOrder = z.infer<typeof PayloadOrder>

/** Payload when updating an order with the in preparation status. */
export const PrepareArticle = ArticleOrderPayload.omit({
    is_weight_quantity: true,
})

/** Payload data to prepare an order. */
export const PrepareOrderPayload = z.object({
    articles: PrepareArticle.array().min(1),
})

export type PrepareOrderPayload = z.infer<typeof PrepareOrderPayload>

/** Payload data to send when canceling an order. */
export const CancelOrderPayload = z.object({
    reason: ReasonCancelOrder,
})

export type CancelOrderPayload = z.infer<typeof CancelOrderPayload>

export const OrderList = BaseOrder.omit({
    history: true,
}).and(OrderUnion)

export type OrderList = z.infer<typeof OrderList>

export const OrdersRequest = Pagination.extend({
    results: OrderList.array(),
})

export type OrdersRequest = z.infer<typeof OrdersRequest>
